import React, { useMemo } from 'react';

import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material';

import useHeroPlannerOfferVisibilities from '~/hooks/customerCommunication/useHeroPlannerOfferVisibilities';

import { Dayjs } from '~/services/TimeService';

import { isRequestUnresolved } from '~/utils/requestUtils';

import { isLuxPlusMonthCampaign } from '../../utils/heroPlannerScheduleUtils';

interface Props {
  scheduleSendDate: Dayjs;
  countryGroupId: string;
  countryId: string;
  membershipId: string;
  offerId: string;
  campaign?: string;
}

function isLuxPlusMembership(membershipId: string) {
  return membershipId === 'LUXPLUS';
}

function formatDateRange(start: Dayjs | undefined, end: Dayjs | undefined) {
  return start && end ? `${start.format('DD/MM/YYYY')} — ${end.format('DD/MM/YYYY')}` : undefined;
}

function isLiveOn(sendDate: Dayjs, start: Dayjs | undefined, end: Dayjs | undefined) {
  return !!start && !!end && sendDate.isBetween(start, end, 'date', '[]');
}

function ScheduleOfferVisibilityBlock({
  scheduleSendDate,
  countryGroupId,
  countryId,
  membershipId,
  offerId,
  campaign,
}: Props) {
  const { offerVisibilitiesReq } = useHeroPlannerOfferVisibilities(countryGroupId, countryId, offerId);
  const isCampaignLuxPlusMonth = isLuxPlusMonthCampaign(campaign, scheduleSendDate);

  const visibilities = useMemo(() => {
    if (!offerVisibilitiesReq || isRequestUnresolved(offerVisibilitiesReq)) {
      return;
    }

    return Object.values(offerVisibilitiesReq.result).map((v) => {
      // The two visibility windows are independent: an offer can be live to everyone, live only to
      // LuxPlus+ members (priority access before general release, or an LuxPlus+ only re-run after
      // general visibility has ended), or not live at all.
      const isLiveToEveryone = isLiveOn(scheduleSendDate, v.start, v.end);
      const isLiveToLuxPlus = isLiveOn(scheduleSendDate, v.luxPlusVisibilityStart, v.luxPlusVisibilityEnd);
      const isLuxPlusPriorityAccess = isLiveToLuxPlus && !isLiveToEveryone;

      // Show the window the send date actually falls in, falling back to the general one for context.
      const dateRange = isLuxPlusPriorityAccess
        ? formatDateRange(v.luxPlusVisibilityStart, v.luxPlusVisibilityEnd)
        : formatDateRange(v.start, v.end);

      // LuxPlus+ Month campaign offers are unlocked for everyone during the campaign window,
      // so the priority-access membership warning no longer applies to them.
      const canAudienceSeeOffer =
        isLiveToEveryone || (isLuxPlusPriorityAccess && (isLuxPlusMembership(membershipId) || isCampaignLuxPlusMonth));

      return {
        countryId: v.countryId,
        dateRange,
        isLuxPlusPriorityAccess,
        shouldShowError: !canAudienceSeeOffer || !dateRange,
      };
    });
  }, [offerVisibilitiesReq, scheduleSendDate, membershipId, isCampaignLuxPlusMonth]);

  if (!visibilities) {
    return (
      <Typography variant="body2">
        <Skeleton width={192} />
      </Typography>
    );
  }

  return (
    <Box>
      {visibilities.map((visibility) => {
        return (
          <Box key={visibility.countryId} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color={visibility.shouldShowError ? 'error' : undefined}>
              <span>{visibility.countryId}: </span>
              {!!visibility.dateRange && <b>{visibility.dateRange}</b>}
              {!visibility.dateRange && <i>Unavailable</i>}
            </Typography>
            {visibility.isLuxPlusPriorityAccess && (
              <Tooltip title="Lux Plus Priority Access">
                <Chip label="LP+" size="small" color="info" />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default ScheduleOfferVisibilityBlock;
